import { useCallback, useRef, useState } from 'react';

import {
  ApplePayRequestPaymentResult,
  IPaymentDetails,
  RequestPaymentInput,
  paymentErrorType,
} from 'features/payment/types';
import { STORE_UNAVAILABLE } from 'features/store/constants';

import { IRequestApplePayPayment, IUseApplePay } from 'state/apple-pay/hooks/types';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { logger } from 'utils/logger';

/**
 * Stores the active promise resolver/rejector for the current payment request.
 *
 * NOTE: This ref is necessary because useApplePay uses a callback pattern instead of Promises.
 * The promise is created in requestPayment() but resolved in handleApplePay() which is called
 * asynchronously by the native Apple Pay module. This is a bridge between the callback-based
 * useApplePay hook and the Promise-based API we want to expose.
 */
type PromiseCallbacks = {
  resolve: (value: IPaymentDetails) => void;
  reject: (error: Error) => void;
};

export const useRequestApplePayPayment = ({
  applePay: applePayInput,
}: RequestPaymentInput): ApplePayRequestPaymentResult => {
  const { billingCountry = ISOs.USA, tip, isForPrepaidReload, serverOrder, reloadAmount } =
    applePayInput ?? {};
  const [data, setData] = useState<IPaymentDetails>();
  const [error, setError] = useState<paymentErrorType>();
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const activePromiseRef = useRef<PromiseCallbacks | null>(null);

  // Helper to clear and resolve/reject the active promise
  const clearPromise = (callback?: (callbacks: PromiseCallbacks) => void) => {
    const callbacks = activePromiseRef.current;
    if (callbacks) {
      activePromiseRef.current = null;
      callback?.(callbacks);
    }
  };

  const handleApplePay: IUseApplePay['handleApplePay'] = async ({
    data: applePayDetails,
    error,
  }) => {
    if (error) {
      setError(paymentErrorType.applePayGeneric);
      clearPromise(callbacks => callbacks.reject(new Error('Apple Pay payment failed')));
      return;
    }

    const paymentDetails: IPaymentDetails = { applePayDetails };
    setData(paymentDetails);
    clearPromise(callbacks => callbacks.resolve(paymentDetails));
  };

  const {
    requestApplePayPayment,
    formatApplePayCartPayload,
    requestingPayment: isLoading,
  } = useApplePay({
    handleApplePay,
    tipAmount: tip,
    prepaid: isForPrepaidReload,
    serverOrder,
  });

  const requestPayment = useCallback((): Promise<IPaymentDetails> => {
    // Cancel any existing promise before creating a new one
    clearPromise(callbacks =>
      callbacks.reject(new Error('New payment request initiated before previous one completed'))
    );

    return new Promise<IPaymentDetails>((resolve, reject) => {
      const applePayParams: IRequestApplePayPayment | null = isForPrepaidReload
        ? {
            countryCode,
            currencyCode,
            total: reloadAmount ?? 0,
          }
        : formatApplePayCartPayload();

      if (!applePayParams) {
        const errorMessage =
          '[useRequestApplePayPayment][requestPayment] Apple Pay params could not be generated.';

        logger.error({
          message: errorMessage,
          isForPrepaidReload,
          reloadAmount,
          billingCountry,
          tip,
          serverOrder,
        });
        setError(paymentErrorType.applePayGeneric);
        reject(new Error(errorMessage));
        return;
      }

      activePromiseRef.current = { resolve, reject };

      try {
        requestApplePayPayment(applePayParams);
      } catch (error) {
        logger.error({
          message:
            '[useRequestApplePayPayment][requestPayment] Error occurred while requesting Apple Pay payment.',
          error,
        });

        const errorType =
          error instanceof Error && error.message === STORE_UNAVAILABLE
            ? paymentErrorType.storeUnavailable
            : paymentErrorType.applePayGeneric;
        setError(errorType);

        clearPromise(callbacks =>
          callbacks.reject(
            error instanceof Error ? error : new Error('Apple Pay payment request failed')
          )
        );
      }
    });
  }, [
    billingCountry,
    countryCode,
    currencyCode,
    formatApplePayCartPayload,
    isForPrepaidReload,
    reloadAmount,
    requestApplePayPayment,
    serverOrder,
    tip,
  ]);

  return { data, isLoading, error, requestPayment };
};
